// Structured Console chat — maps Grant's sentences onto existing Console /
// v4-proxy routes. No LLM. SPA /chat owns this page; do not mount Express HTML.

export const HELP = [
  'show store lincoln-fb          — open one store (same command for every id)',
  'list stores                    — GET /api/active-stores',
  'use store lincoln-fb           — pin a store; later commands reuse it',
  'what did we pay out last quarter',
  'payouts for lincoln-fb',
].join('\n');

// The sentence parses, but POST /api/team-stores/drops has no service behind it
// on main — scheduled drops ship with the unmerged fleet PR (#355). Say that
// plainly instead of firing a request that 404s and reads like an outage.
export const DROP_UNAVAILABLE = 'Scheduled drops are not available yet — the fleet rollout service is not deployed. '
  + 'Nothing was scheduled.';

/** Parse the sentence (kept whole for #355), then refuse to dispatch it. */
function dropCommand(parsed: ParsedCommand): ParsedCommand {
  if (parsed.type === 'schedule-drop') return { type: 'unavailable', message: DROP_UNAVAILABLE };
  return parsed;
}

const MONTHS: Record<string, number> = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

const STORE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{2,79}$/;

export type ChatContext = { storeId?: string };

export type ParsedCommand =
  | { type: 'list-stores' }
  | { type: 'open-store'; storeId: string }
  | { type: 'schedule-drop'; templateId: string; productTemplateId: string; scheduledForLocal: string; name: string }
  // A command we understood but cannot run against the services on main.
  | { type: 'unavailable'; message: string }
  | { type: 'list-payouts'; period: 'last' | 'this' | 'all'; storeId?: string; year?: number; quarter?: number }
  | { type: 'help'; message: string }
  | { type: 'unknown'; message: string };

export type ApiCall = { method: 'GET' | 'POST'; path: string; body?: Record<string, string> };

export type PayoutRow = {
  id?: string;
  storeId?: string;
  storeName?: string;
  year?: number | null;
  quarter?: number | null;
  teamCommission?: number;
  salesRepCommission?: number;
  revenue?: number;
};

function normalize(text: string): string {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

export function calendarQuarter(now: Date): { year: number; quarter: number } {
  return { year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1 };
}

export function lastQuarter(now: Date): { year: number; quarter: number } {
  const cur = calendarQuarter(now);
  if (cur.quarter === 1) return { year: cur.year - 1, quarter: 4 };
  return { year: cur.year, quarter: cur.quarter - 1 };
}

function looksLikeStoreId(token: string): boolean {
  const t = String(token || '').trim();
  return STORE_ID_RE.test(t) && !/^(stores?|list|show|open|use|drop|payouts?|list-stores|list-payouts|open-store|schedule-drop|use-store|template|last|this|quarter|help)$/i.test(t);
}

function extractStoreToken(text: string): string {
  const raw = normalize(text);
  const quoted = raw.match(/["“]([^"”]+)["”]/);
  if (quoted) return quoted[1].trim();
  const m = raw.match(/\b(?:store|for)\s+([A-Za-z0-9][A-Za-z0-9_-]{2,79})\b/i);
  if (m) return m[1];
  const ts = raw.match(/\b(TS-\d+)\b/i);
  if (ts) return ts[1];
  return '';
}

function parseClock(chunk: string): { hour: number; minute: number } | null {
  const t = String(chunk || '').trim().toLowerCase();
  if (!t) return { hour: 8, minute: 0 };
  const ampm = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
  if (ampm) {
    let hour = Number(ampm[1]);
    const minute = Number(ampm[2] || 0);
    const mer = ampm[3];
    if (hour === 12) hour = mer === 'am' ? 0 : 12;
    else if (mer === 'pm') hour += 12;
    if (hour > 23 || minute > 59) return null;
    return { hour, minute };
  }
  const hm = t.match(/^(\d{1,2}):(\d{2})$/);
  if (hm) {
    const hour = Number(hm[1]);
    const minute = Number(hm[2]);
    if (hour > 23 || minute > 59) return null;
    return { hour, minute };
  }
  const bare = t.match(/^(\d{1,2})$/);
  if (bare) {
    const hour = Number(bare[1]);
    if (hour > 23) return null;
    return { hour, minute: 0 };
  }
  return null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function localStamp(year: number, month: number, day: number, hour: number, minute: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`;
}

export function parseWhen(text: string, now: Date): { scheduledForLocal?: string; error?: string } {
  const raw = normalize(text);
  if (!raw) return { error: 'Say when — e.g. Nov 1 8am (America/Chicago).' };

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
  if (iso) {
    const hour = iso[4] != null ? Number(iso[4]) : 8;
    const minute = iso[5] != null ? Number(iso[5]) : 0;
    return { scheduledForLocal: localStamp(Number(iso[1]), Number(iso[2]), Number(iso[3]), hour, minute) };
  }

  const named = raw.match(
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?(?:\s+(?:at\s+)?(\d{1,2}(?::\d{2})?\s*(?:am|pm)?|\d{1,2}:\d{2}))?/i,
  );
  if (!named) return { error: `Could not read a date from “${raw}”. Try Nov 1 8am or 2026-11-01T08:00.` };

  const month = MONTHS[named[1].toLowerCase()];
  const day = Number(named[2]);
  const clock = parseClock(named[4] || '8am');
  if (!month || day < 1 || day > 31 || !clock) {
    return { error: `Could not read a date from “${raw}”.` };
  }
  let year = named[3] ? Number(named[3]) : now.getFullYear();
  if (!named[3]) {
    const candidate = new Date(year, month - 1, day, clock.hour, clock.minute);
    if (candidate.getTime() <= now.getTime()) year += 1;
  }
  return { scheduledForLocal: localStamp(year, month, day, clock.hour, clock.minute) };
}

function dropName(templateId: string, scheduledForLocal: string): string {
  return `${templateId} ${String(scheduledForLocal || '').slice(0, 10)}`.trim();
}

function parsePayouts(text: string, context: ChatContext, now: Date): ParsedCommand {
  const raw = normalize(text);
  const lower = raw.toLowerCase();
  const storeId = extractStoreToken(raw) || context.storeId || '';
  let period: 'last' | 'this' | 'all' = 'all';
  let yq: { year: number; quarter: number } | null = null;
  if (/\blast\s+quarter\b/.test(lower) || /\bprior\s+quarter\b/.test(lower)) {
    period = 'last';
    yq = lastQuarter(now);
  } else if (/\bthis\s+quarter\b/.test(lower) || /\bcurrent\s+quarter\b/.test(lower)) {
    period = 'this';
    yq = calendarQuarter(now);
  }
  const out: Extract<ParsedCommand, { type: 'list-payouts' }> = { type: 'list-payouts', period };
  if (storeId) out.storeId = storeId;
  if (yq) {
    out.year = yq.year;
    out.quarter = yq.quarter;
  }
  return out;
}

function parseDrop(text: string, now: Date): ParsedCommand {
  const raw = normalize(text);
  let templateId = '';
  let whenText = '';
  const named = raw.match(/\btemplate\s+([A-Za-z0-9][A-Za-z0-9_-]{0,79})\b/i);
  if (named) {
    templateId = named[1];
    const after = raw.match(/\btemplate\s+[A-Za-z0-9][A-Za-z0-9_-]{0,79}\s+(?:for|on)\s+(.+)$/i);
    const forOnly = raw.match(/\bfor\s+(.+)$/i);
    whenText = after?.[1] || forOnly?.[1] || '';
  } else {
    const onFor = raw.match(/\bon\s+([A-Za-z0-9][A-Za-z0-9_-]{0,79})\s+for\s+(.+)$/i);
    if (onFor) {
      templateId = onFor[1];
      whenText = onFor[2];
    } else {
      const parts = raw.match(/^([A-Za-z0-9][A-Za-z0-9_-]{0,79})\s+(.+)$/);
      if (parts) {
        templateId = parts[1];
        whenText = parts[2];
      }
    }
  }
  if (!templateId) {
    return { type: 'unknown', message: 'Schedule a drop on which template? Example: schedule a drop on template general for Nov 1 8am' };
  }
  const parsed = parseWhen(whenText, now);
  if (parsed.error || !parsed.scheduledForLocal) return { type: 'unknown', message: parsed.error || 'Need a date.' };
  return {
    type: 'schedule-drop',
    templateId,
    productTemplateId: templateId,
    scheduledForLocal: parsed.scheduledForLocal,
    name: dropName(templateId, parsed.scheduledForLocal),
  };
}

export function parseCommand(text: string, ctx: ChatContext = {}, now: Date = new Date()): ParsedCommand {
  const raw = normalize(text);
  const context = ctx || {};
  if (!raw) return { type: 'help', message: HELP };

  const lower = raw.toLowerCase();
  if (lower === 'help' || lower === '?' || lower === '/help') {
    return { type: 'help', message: HELP };
  }
  if (lower === 'list-stores') return { type: 'list-stores' };
  if (lower === 'list-payouts') return parsePayouts('', context, now);
  if (lower === 'open-store') {
    if (!context.storeId) return { type: 'unknown', message: 'open-store needs a store id. Example: show store lincoln-fb' };
    return { type: 'open-store', storeId: context.storeId };
  }

  const slash = raw.match(/^\/([a-z-]+)(?:\s+(.+))?$/i);
  if (slash) {
    const cmd = slash[1].toLowerCase();
    const rest = (slash[2] || '').trim();
    if (cmd === 'stores' || cmd === 'list-stores') return { type: 'list-stores' };
    if (cmd === 'store' || cmd === 'open-store' || cmd === 'open') {
      const storeId = rest || context.storeId || '';
      if (!storeId) return { type: 'unknown', message: 'open-store needs a store id. Example: /store lincoln-fb' };
      return { type: 'open-store', storeId };
    }
    if (cmd === 'use' || cmd === 'use-store') {
      if (!rest) return { type: 'unknown', message: 'use-store needs a store id.' };
      return { type: 'open-store', storeId: rest };
    }
    if (cmd === 'payouts' || cmd === 'list-payouts') return parsePayouts(rest, context, now);
    if (cmd === 'drop' || cmd === 'schedule-drop') return dropCommand(parseDrop(rest, now));
    if (cmd === 'help') return { type: 'help', message: HELP };
  }

  if (/\b(schedule|plan)\b/.test(lower) && /\bdrop\b/.test(lower)) return dropCommand(parseDrop(raw, now));
  if (/^drop\b/.test(lower) && /\b(template|for|on)\b/.test(lower)) return dropCommand(parseDrop(raw, now));

  if (/\b(payouts?|paid out|pay out|paid)\b/.test(lower)) return parsePayouts(raw, context, now);

  if (/\b(use|working on)\s+store\b/.test(lower) || /^use\s+\S+/.test(lower)) {
    const storeId = extractStoreToken(raw) || raw.replace(/^use\s+(?:store\s+)?/i, '').trim();
    if (!storeId) return { type: 'unknown', message: 'Say which store to use.' };
    return { type: 'open-store', storeId };
  }

  if (/\b(show|open)\s+store\b/.test(lower) || /^store\s+\S+/.test(lower)) {
    const storeId = extractStoreToken(raw) || raw.replace(/^(?:show|open)\s+store\s+/i, '').replace(/^store\s+/i, '').trim();
    if (!storeId) {
      if (context.storeId) return { type: 'open-store', storeId: context.storeId };
      return { type: 'unknown', message: 'Show which store? Example: show store lincoln-fb' };
    }
    return { type: 'open-store', storeId };
  }

  if (
    /^(list|show|get)\s+(all\s+)?(the\s+)?stores?\b/.test(lower)
    || /^(what|which)\s+stores?\b/.test(lower)
    || lower === 'stores'
    || lower === 'list stores'
    || lower === 'show stores'
  ) {
    return { type: 'list-stores' };
  }

  if (looksLikeStoreId(raw) && !/\s/.test(raw)) {
    return { type: 'open-store', storeId: raw };
  }

  return { type: 'unknown', message: `I don’t have a command for that yet.\n\n${HELP}` };
}

export function apiCallFor(parsed: ParsedCommand): ApiCall | null {
  if (parsed.type === 'list-stores') {
    return { method: 'GET', path: '/api/active-stores?limit=2000' };
  }
  if (parsed.type === 'open-store') {
    return { method: 'GET', path: `/api/active-stores/${encodeURIComponent(parsed.storeId)}` };
  }
  // schedule-drop deliberately has no ApiCall: POST /api/team-stores/drops is
  // not proxied on main. parseCommand converts it to 'unavailable' before it
  // reaches here; the shape stays so #355 only has to restore this branch.
  if (parsed.type === 'list-payouts') {
    if (parsed.storeId) {
      return { method: 'GET', path: `/api/team-stores/${encodeURIComponent(parsed.storeId)}/payouts` };
    }
    return { method: 'GET', path: '/api/payouts' };
  }
  return null;
}

function periodFromPayout(row: PayoutRow): { year: number; quarter: number } | null {
  if (row.year && row.quarter) return { year: Number(row.year), quarter: Number(row.quarter) };
  const m = /_(\d{4})Q([1-4])$/.exec(String(row.id || ''));
  if (m) return { year: Number(m[1]), quarter: Number(m[2]) };
  return null;
}

export function filterPayouts(rows: PayoutRow[], parsed: Extract<ParsedCommand, { type: 'list-payouts' }>): PayoutRow[] {
  let out = Array.isArray(rows) ? rows.slice() : [];
  if (parsed.storeId) out = out.filter((r) => String(r.storeId || '') === String(parsed.storeId));
  if (parsed.year && parsed.quarter) {
    out = out.filter((r) => {
      const pq = periodFromPayout(r);
      return pq && pq.year === parsed.year && pq.quarter === parsed.quarter;
    });
  }
  out.sort((a, b) => {
    const ak = (Number(a.year) || 0) * 10 + (Number(a.quarter) || 0);
    const bk = (Number(b.year) || 0) * 10 + (Number(b.quarter) || 0);
    return bk - ak || String(b.id || '').localeCompare(String(a.id || ''));
  });
  return out;
}

export function dollars(n?: number | null): string {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function periodLabel(row: PayoutRow): string {
  const pq = periodFromPayout(row);
  if (pq) return `${pq.year} Q${pq.quarter}`;
  return row.id || '—';
}

export function storeTitle(store: { fundraiserName?: string; schoolName?: string; storeNumberDisplay?: string; id?: string }): string {
  return store.fundraiserName || store.schoolName || store.storeNumberDisplay || store.id || '—';
}

export function missingHubHint(status: number, parsed: ParsedCommand): string {
  if (status !== 404) return '';
  if (parsed.type === 'schedule-drop') return DROP_UNAVAILABLE;
  if (parsed.type === 'list-payouts') {
    return 'GET /api/payouts is served by the Console v4 proxy. A 404 here means this Console build predates it — redeploy rather than assuming nobody was paid.';
  }
  return '';
}
